import { BoostSubscription } from '@eventbrite/boost-subscriptions-and-trials';
import { track } from '@eventbrite/datalayer-library';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import {
    BillingPeriodInterval,
    determineLocaleFromTld,
    EVENT_CAPACITY_MAX,
    Features,
    fetchPlans,
    FLEX_CAMPAIGN_DAILY_LIMIT,
    FLEX_FREE_TIER_CAPACITY,
    getPlanPricingObject,
    PlanOption,
    PricingAndPackagingFeatureFlags,
    PricingAndPackagingSubscriptionStatus,
    SubscriptionStatusInfo,
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_CANCELED,
    SUBSCRIPTION_STATUS_INCOMPLETE,
} from '@eventbrite/pricing-and-packaging-api';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import {
    BASE_FEATURES,
    HIGH_TICKET_SALES_MINIMUM_RATIO,
    RECURRING_MONTHLY_SUBSCRIPTION,
} from '../constants';
import { PlanCheckoutAction } from '../enums';
import { PlanContext } from '../types';

export * from './getRemovePricingAndPackagingStatus';
export * from './heap';
export * from './planUtils';
export * from './testUtils';
export * from './uiUtils';
export * from './useQueryUtils';

export const formatDayMonthAndYear = (dateObject: Date) => {
    const dateOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    };

    const userLanguage = navigator.language || 'default';
    const dateString = dateObject.toLocaleString(userLanguage, dateOptions);
    return dateString;
};

export const calculateHoursLeft = (startTimeInMs: number) => {
    const currentTime = Date.now();
    const elapsedHours = differenceInHours(currentTime, startTimeInMs);
    const remainingMinutes =
        differenceInMinutes(currentTime, startTimeInMs) % 60;
    const remainingHours = 24 - elapsedHours - remainingMinutes / 60;

    return remainingHours.toFixed(2);
};

export const composeExpirationDate = (subscription?: BoostSubscription) => {
    const expirationDateString =
        subscription?.next_payment_date || subscription?.expiration_date;
    const expirationDateObject = new Date(
        expirationDateString || 1696890042 * 1000,
    );

    const expirationDate = formatDayMonthAndYear(expirationDateObject);
    return expirationDate;
};

export const getListingFeeText = (ticketLimit?: number | string) => {
    if (ticketLimit === 'unlimited') {
        return gettext('Host unlimited events, no matter the size');
    } else {
        return gettext(
            'Host unlimited events with up to %(ticketLimit)s tickets',
            { ticketLimit },
        );
    }
};

export const getEmailCapacityText = (emailLimit?: number | string) => {
    const emailLimitString = Number(emailLimit).toLocaleString();
    return gettext('Send %(emailLimitString)s marketing emails a day', {
        emailLimitString,
    });
};

export const getPlanFeatures = (
    plan: PlanOption | null | undefined,
    isMigrationModal?: boolean,
) => {
    const ticketLimit = plan?.features?.event_capacity;
    const emailLimit = plan?.features?.campaign_daily_limit;

    const listingFeeText = getListingFeeText(ticketLimit);
    const emailCapacityText = getEmailCapacityText(emailLimit);

    return [
        ...(isMigrationModal ? [RECURRING_MONTHLY_SUBSCRIPTION] : []),
        listingFeeText,
        emailCapacityText,
        ...BASE_FEATURES,
    ];
};

export const getUserLocale = () => {
    let userLocale = 'US';

    if (window?.__SERVER_DATA__?.locale) {
        userLocale = window?.__SERVER_DATA__?.locale.split('_').pop();
    } else if (HAS_WINDOW) {
        userLocale = determineLocaleFromTld(window.location.hostname);
    }

    return userLocale;
};

export const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getPricingOptions = async (options?: {
    featureFlags?: PricingAndPackagingFeatureFlags;
}) => {
    const [plans] = await Promise.all([
        fetchPlans({ featureFlags: options?.featureFlags }),
    ]);

    return { plans };
};

export const addHtml = (code: any) => ({ __html: code?.toString() });

export const generateBulletPointsFromFeatures = ({
    context,
    features,
    interval,
    options,
}: {
    context: PlanContext;
    features?: Features;
    interval?: string;
    options?: { featureFlags?: PricingAndPackagingFeatureFlags };
}) => {
    let bullets: string[];

    // 'free' is the only context that doesn't require features
    if (!features && context !== 'free') return [];

    const {
        campaign_daily_limit = FLEX_CAMPAIGN_DAILY_LIMIT,
        event_capacity = FLEX_FREE_TIER_CAPACITY,
    } = features || {};

    const isHighestTier =
        event_capacity === 'unlimited' || event_capacity === EVENT_CAPACITY_MAX;

    const flexEventsBullet = isHighestTier
        ? gettext('Host one event, no matter the size').toString()
        : gettext('Host one event with up to %(event_capacity)s tickets', {
              event_capacity,
          }).toString();

    const proEventsBullet = isHighestTier
        ? gettext('Host unlimited events, no matter the size').toString()
        : gettext(
              'Host unlimited events with up to %(event_capacity)s tickets',
              { event_capacity },
          ).toString();

    const emailLimit = Number(campaign_daily_limit).toLocaleString();

    const emailsBullet = gettext('Send %(emailLimit)s marketing emails a day', {
        emailLimit,
    }).toString();

    const allInOne = gettext(
        'Access all-in-one event ticketing and marketing tools',
    ).toString();

    const freeFlex = [flexEventsBullet, emailsBullet, allInOne];
    const freeBullets = [
        gettext('Publish unlimited events of any size').toString(),
        gettext('Custom event pages with multiple ticket types').toString(),
        gettext('Flexible payouts').toString(),
        gettext("Visibility on Eventbrite's marketplace").toString(),
        gettext('Detailed analytics and reporting').toString(),
        gettext('Built-in marketing tools').toString(),
        gettext('250 marketing email sends a day').toString(),
        gettext('Help Center and email support').toString(),
        gettext('24/7 chat support for paid events').toString(),
    ];

    switch (context) {
        case 'free':
            bullets = options?.featureFlags?.enableRemovePricingAndPackaging
                ? freeBullets
                : freeFlex;
            break;

        case 'subscription':
            bullets = options?.featureFlags?.enableRemovePricingAndPackaging
                ? [
                      gettext('Up to %(emailLimit)s daily marketing emails', {
                          emailLimit,
                      }).toString(),
                      gettext('24/7 chat support for any event').toString(),
                      gettext('Cancel anytime').toString(),
                  ]
                : [
                      gettext('Recurring %(interval)sly subscription', {
                          interval: interval || 'month',
                      }).toString(),
                      proEventsBullet,
                      emailsBullet,
                      allInOne,
                  ];
            break;

        case 'success':
            bullets = options?.featureFlags?.enableRemovePricingAndPackaging
                ? [emailsBullet, allInOne]
                : [proEventsBullet, emailsBullet, allInOne];
            break;

        default:
            bullets = [];
            break;
    }

    return bullets;
};

export const getProPlanOptions = async (options?: {
    featureFlags?: PricingAndPackagingFeatureFlags;
}) => {
    try {
        const plans = await fetchPlans(options);

        return plans;
    } catch (error) {
        throw error;
    }
};

type RecommendedPlanName = 'Pro 100' | 'Pro 250' | 'Pro Unlimited' | null;

export const getRecommendedPlan = (
    boostSubscription: any,
    planOptions: PlanOption[] | undefined,
) => {
    let recommendedPlanName: RecommendedPlanName;
    let boostMigrationRecommededPlanName;

    if (HAS_WINDOW) {
        boostMigrationRecommededPlanName = window.localStorage?.getItem(
            'boostMigrationRecommededPlanName',
        ) as RecommendedPlanName;
    }

    if (boostSubscription?.plan_id?.includes('new-starter')) {
        recommendedPlanName = 'Pro 100';
    } else if (boostSubscription?.plan_id?.includes('new-basic')) {
        recommendedPlanName = 'Pro 250';
    } else if (boostSubscription?.plan_id?.includes('new-pro')) {
        recommendedPlanName = 'Pro Unlimited';
    } else if (boostMigrationRecommededPlanName) {
        recommendedPlanName = boostMigrationRecommededPlanName;
    } else {
        recommendedPlanName = null;
    }

    const recommendedPlan: PlanOption | undefined = planOptions?.find(
        (option) => option.plan_name === recommendedPlanName,
    );

    if (HAS_WINDOW && recommendedPlanName) {
        window.localStorage?.setItem(
            'boostMigrationRecommededPlanName',
            recommendedPlanName,
        );
    }

    return { recommendedPlanName, recommendedPlan };
};

// All migrating orgs are in USD for now
export const getUserLocaleAndMigrationCurrency = () => {
    let userLocale = 'US';
    const currency = 'USD';

    if (window?.__SERVER_DATA__?.locale) {
        userLocale = window?.__SERVER_DATA__?.locale.split('_').pop();
    } else if (HAS_WINDOW) {
        userLocale = determineLocaleFromTld(window.location.hostname);
    }

    return { userLocale, currency };
};

export const trackHeapRecommendedAndSelectedPlans = (
    eventName: string,
    recommendedPlanName: string,
    selectedPlanName?: string,
) => {
    const heapData: { recommendedPlan: string; planSelection?: string } = {
        recommendedPlan: recommendedPlanName,
    };

    if (recommendedPlanName && selectedPlanName) {
        heapData.planSelection = selectedPlanName;
    }

    track({
        eventName: eventName,
        eventData: heapData,
    });
};

// For future discounts on plans, currently just for NPO
export const checkPrefixForDiscountedPlan = (
    subscriptionPlanName?: string,
    selectedPlanName?: string,
    subscriptionPlanStatus?: PricingAndPackagingSubscriptionStatus | null,
) => {
    return (
        'NPO ' + subscriptionPlanName == selectedPlanName &&
        subscriptionPlanStatus != SUBSCRIPTION_STATUS_CANCELED
    );
};

export const getPlanCheckoutAction = ({
    activeSubscription,
    selectedPlan,
    planCurrency,
    billingPeriodInterval,
}: {
    activeSubscription?: SubscriptionStatusInfo;
    selectedPlan: PlanOption;
    planCurrency: string;
    billingPeriodInterval: BillingPeriodInterval;
}): PlanCheckoutAction => {
    const selectedPlanPricing = getPlanPricingObject(
        selectedPlan,
        planCurrency,
        billingPeriodInterval,
    );

    const { subscription, subAndTrialStatus } = activeSubscription || {};

    const selectedPlanPriceAmount =
        selectedPlanPricing?.currency_options?.[planCurrency.toLowerCase()]
            ?.value;

    const currentPlanPriceAmount = subscription?.price;

    const isPriceIncreasing = !!(
        Number(selectedPlanPriceAmount) > Number(currentPlanPriceAmount)
    );

    const isIncompleteSubscription =
        subscription?.status === SUBSCRIPTION_STATUS_INCOMPLETE;

    const isActiveSubscription =
        subscription?.status === SUBSCRIPTION_STATUS_ACTIVE;

    const isTrialing = !!subAndTrialStatus?.is_trialing;

    const isUpdatingCurrentPlan =
        (!!subscription?.plan_name &&
            !isTrialing &&
            subscription?.status !== SUBSCRIPTION_STATUS_CANCELED &&
            (subscription?.plan_name !== selectedPlan?.plan_name ||
                selectedPlanPricing?.period?.interval !==
                    subscription?.interval)) ||
        isIncompleteSubscription;

    const isUpgradingCurrentPlan = !!(
        (Number(selectedPlan?.features?.campaign_daily_limit) >
            Number(subscription?.features?.campaign_daily_limit) &&
            subscription?.interval !== 'year' &&
            selectedPlanPricing?.period.interval !== 'month' &&
            isActiveSubscription) ||
        (isUpdatingCurrentPlan && isPriceIncreasing)
    );

    const hasScheduledCancelation =
        !!subAndTrialStatus?.is_subscription_set_to_cancel;

    const isAlreadySubscribedToSelectedPlan = !!(
        subscription?.plan_name == selectedPlan?.plan_name &&
        selectedPlanPricing?.period?.interval === subscription?.interval &&
        subscription?.status === SUBSCRIPTION_STATUS_ACTIVE
    );

    const isDiscountedCurrentPlan = checkPrefixForDiscountedPlan(
        subscription?.plan_name,
        selectedPlan?.plan_name,
        subscription?.status,
    );

    const isDowngradingCurrentPlan =
        !!(
            isUpdatingCurrentPlan &&
            subscription?.features?.campaign_daily_limit &&
            selectedPlan?.features?.campaign_daily_limit &&
            Number(subscription?.features?.campaign_daily_limit) >
                Number(selectedPlan?.features?.campaign_daily_limit)
        ) ||
        (subscription?.interval === 'year' &&
            selectedPlanPricing?.period.interval === 'month');

    const isResubscribingToSelectedPlan =
        isAlreadySubscribedToSelectedPlan && hasScheduledCancelation;

    if (isUpgradingCurrentPlan) {
        return PlanCheckoutAction.UPGRADE_PLAN;
    } else if (isDowngradingCurrentPlan || isDiscountedCurrentPlan) {
        return PlanCheckoutAction.DOWNGRADE_PLAN;
    } else if (isResubscribingToSelectedPlan) {
        return PlanCheckoutAction.RESUBSCRIBE;
    } else {
        return PlanCheckoutAction.SUBSCRIBE;
    }
};

export const getIsUserInSubscriptionSupportedTld = () => {
    // this is a copy of getRemovePricingAndPackagingStatus, that will be replaced in a follow-up PR
    const tldAllowedList = [
        '.com',
        '.co.uk',
        '.com.au',
        '.ie',
        '.co.nz',
        '.ca',
    ];
    const userTld = HAS_WINDOW && window?.__SERVER_DATA__?.env?.localeInfo?.tld;

    return Boolean(tldAllowedList.find((tld) => userTld === tld));
};

export const updateAddCapacityBannerStatus = ({
    startTime,
    setShowAddCapacityBanner,
    ticketsSold,
    totalTickets,
    isOnEventDashboard,
}: {
    startTime: string | null | undefined;
    setShowAddCapacityBanner: Function;
    ticketsSold: number | undefined;
    totalTickets: number | undefined;
    isOnEventDashboard?: boolean;
}) => {
    let isEventMoreThanOneDayInFuture = false;

    if (startTime) {
        const now = new Date();
        const tomorrowTimestamp = new Date(
            now.setDate(now.getDate() + 1),
        ).getTime();
        const eventStartTimestamp = new Date(startTime).getTime();
        isEventMoreThanOneDayInFuture = eventStartTimestamp > tomorrowTimestamp;
    }

    if (
        ticketsSold &&
        totalTickets &&
        ticketsSold / totalTickets >= HIGH_TICKET_SALES_MINIMUM_RATIO &&
        isEventMoreThanOneDayInFuture
    ) {
        track({
            eventName: isOnEventDashboard
                ? 'TicketSellingFastBannerView'
                : 'YourNextEventCardView',
            eventData: {},
        });

        setShowAddCapacityBanner(true);
    }
};
